import { LimitedEditionRelated } from "./LimitedEditionRelated";
import { LimitedEditionShowcaseRow } from "./LimitedEditionShowcaseRow";
import styles from "./index.module.scss";

type InfoItem = {
	title?: string;
	text?: string;
	textAlignment?: string | null;
	image?: {
		url?: string;
		alt?: string | null;
		format?: string;
		width?: number;
		height?: number;
	};
};

type MoodGalleryImage = {
	id?: string;
	url?: string;
	alt?: string | null;
	responsiveImage?: unknown;
};

type ShowcaseItem = {
	gallery?: Array<{
		id?: string;
		url?: string;
		alt?: string | null;
		responsiveImage?: unknown;
	}>;
	info?: InfoItem[];
	hideGallery?: boolean;
	hasMoodGallery?: boolean;
	moodGallery?: MoodGalleryImage[];
};

type RelatedEdition = {
	slug?: string;
	hero?: {
		__typename?: string;
		title?: string;
		featuredImage?: { url?: string; responsiveImage?: unknown };
		backgroundImage?: { url?: string; responsiveImage?: unknown };
		image?: { url?: string; responsiveImage?: unknown };
	};
};

type LimitedEditionSectionProps = {
	sectionBgColor?: { color?: { hex?: string } };
	textColor?: { color?: { hex?: string; cssRgb?: string } };
	iconColor?: { color?: { hex?: string } };
	showRelatedEditions?: boolean;
	relatedTextColor?: { color?: { hex?: string; cssRgb?: string } };
	relatedBgColor?: { color?: { hex?: string; cssRgb?: string } };
	relatedButtonConfig?: any;
	limitedEdtLink?: RelatedEdition[];
	showcase?: ShowcaseItem[];
};

export default function LimitedEditionSection({
	sectionBgColor,
	textColor,
	iconColor,
	showRelatedEditions,
	relatedTextColor,
	relatedBgColor,
	relatedButtonConfig,
	limitedEdtLink,
	showcase,
}: LimitedEditionSectionProps) {
	const showcaseItems = (showcase ?? []).filter((item) => {
		const moodGallery = item?.moodGallery ?? [];
		const showMoodGallery =
			item?.hasMoodGallery && moodGallery.length > 0;
		return (
			(item?.gallery?.length ?? 0) > 0 ||
			(item?.info?.length ?? 0) > 0 ||
			showMoodGallery
		);
	});

	const hasRelated =
		showRelatedEditions && (limitedEdtLink?.length ?? 0) > 0;

	if (!showcaseItems.length && !hasRelated) return null;

	const sectionBgHex = sectionBgColor?.color?.hex;
	const relatedBgHex = relatedBgColor?.color?.hex;

	return (
		<section
			className={styles.section}
			style={{ backgroundColor: sectionBgHex }}
		>
			{showcaseItems.map((item, index) => (
				<LimitedEditionShowcaseRow
					key={index}
					item={item}
					inverted={index % 2 === 1}
					sectionBgHex={sectionBgHex}
					textColor={textColor}
					iconColor={iconColor}
				/>
			))}
			{hasRelated && (
				<div
					className={styles.relatedRow}
					style={{ backgroundColor: relatedBgHex ?? "#ffffff" }}
				>
					<LimitedEditionRelated
						editions={limitedEdtLink}
						textColor={
							relatedTextColor?.color ?? textColor?.color
						}
						buttonConfig={relatedButtonConfig}
					/>
				</div>
			)}
		</section>
	);
}

export const fragment = `
  fragment LimitedEditionSectionFragment on LimitedEdtSectionRecord {
    id
    name
    showRelatedEditions
    textColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    sectionBgColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    iconColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    relatedTextColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    relatedBgColor {
      id
      name
      color {
        hex
        cssRgb
      }
    }
    relatedButtonConfig {
      id
      buttonStyle
      addIcon
      labelColor {
        color {
          hex
          cssRgb
        }
      }
      typeColor {
        color {
          hex
          cssRgb
        }
      }
      iconColor {
        color {
          hex
        }
      }
      icon {
        img {
          url
          width
          height
        }
      }
    }
    limitedEdtLink {
      slug
      hero {
        __typename
        ... on SectionHeroFeaturedRecord {
          title
          featuredImage {
            url
            alt
            format
            focalPoint {
              x
              y
            }
            responsiveImage(imgixParams: { auto: format, q: 85, w: 600 }) {
              ...responsiveImageFragment
            }
          }
          backgroundImage {
            url
            alt
            format
            responsiveImage(imgixParams: { auto: format, q: 85, w: 600 }) {
              ...responsiveImageFragment
            }
          }
        }
        ... on SectionHeroImageRecord {
          title
          image {
            url
            alt
            format
            focalPoint {
              x
              y
            }
            responsiveImage(imgixParams: { auto: format, q: 85, w: 600 }) {
              ...responsiveImageFragment
            }
          }
        }
      }
    }
    showcase {
      hideGallery
      hasMoodGallery
      moodGallery {
        id
        url
        alt
        format
        responsiveImage(imgixParams: { auto: format, q: 85, w: 1200 }) {
          ...responsiveImageFragment
        }
      }
      gallery {
        id
        url
        alt
        format
        responsiveImage(imgixParams: { auto: format, q: 85, w: 800 }) {
          ...responsiveImageFragment
        }
      }
      info {
        title
        text
        textAlignment
        image {
          url
          alt
          format
          width
          height
        }
      }
    }
  }
`;
